import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FC, useEffect, useMemo, useCallback } from "react";
import AppRoutes from "@/constants/routes/AppRoutes";
import PageContent from "../PageContent";
import useLegislation from "@/hooks/useLegislation";
import {
  DocumentCategory,
  LegislationStatus,
} from "@/models/legislation/Legislation";
import DocumentSidebar from "@/components/content-sidebar/document-sidebar/document-sidebar";
import { DocumentTab } from "@/components/content-sidebar/document-sidebar/document-tab-triggers";
import EmbedPDFViewer from "@/partials/pdf-viewer/EmbedPDFViewer";
import { DocumentPDFHeader } from "../chat/components/LegislationPDFHeader";
import { useDocumentReferenceStore } from "@/stores/document-reference.store";
import { useLegislationTableStore } from "@/stores/legislation-table-state.store";
import { useDatasourceBreadcrumbReturn } from "@/pages/legislation/hooks/useDatasourceBreadcrumbReturn";
import { getViewConfigFromPath } from "@/config/documentViewConfig";
import { buildPdfUrlFromReference } from "./utils/buildPdfUrl";
import useLegislationPdfUrl from "@/hooks/useLegislationPdfUrl";
import { normalizeDataSource } from "@/config/datasources";
import DeprecatedConsolidationDatasourcePage from "./components/DeprecatedConsolidationDatasourcePage";
import { isDeprecatedSilverConsolidationDatasource } from "@/lib/deprecatedDatasources";
import { resolveLegislationViewerReturnUrl } from "@/lib/navigation/documentViewerReturn";
import { useDocumentViewerBreadcrumbs } from "@/hooks/useDocumentViewerBreadcrumbs";
import { useDocumentViewerStackSync } from "@/hooks/useDocumentViewerContextSync";

/**
 * Unified component for rendering legislation PDF content.
 * Supports both chat context (from conversation references) and
 * legislation context (from legislation browse).
 *
 * The context is determined automatically from the route path.
 */
const UnifiedLegislationPDFView: FC = () => {
  const { documentId, datasource } = useParams<{
    documentId: string;
    datasource: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Get configuration based on current route
  const config = useMemo(
    () => getViewConfigFromPath(location.pathname, "pdf"),
    [location.pathname]
  );

  const isChatContext = config.context === "chat";
  const isDeprecatedDatasource = isDeprecatedSilverConsolidationDatasource(
    datasource
  );

  // Stores
  const {
    selectedDocumentReference,
    selectedBoundingBoxes,
    documentReferences,
  } = useDocumentReferenceStore();
  const { setNavigateBack } = useLegislationTableStore();

  // Data fetching
  const { useGetByExternalId } = useLegislation();

  // In chat context, use datasource from selectedDocumentReference if available
  // In legislation context, use datasource from URL params
  const rawDatasource = isChatContext
    ? selectedDocumentReference?.source || datasource || ""
    : datasource || "";

  // Normalize datasource to ensure we use the correct database identifier
  // This handles cases where chat references may have variant spellings
  const normalizedDataSource = normalizeDataSource(rawDatasource);
  const effectiveDatasource = normalizedDataSource || rawDatasource;

  const legislationQuery = useGetByExternalId(documentId, effectiveDatasource);
  const legislation = legislationQuery.data ?? null;

  // Redirect if no documentId
  useEffect(() => {
    if (!documentId) {
      const notFoundRoute = isChatContext
        ? AppRoutes.chatFileNotFound
        : AppRoutes.legislationFileNotFound;
      navigate(notFoundRoute, { replace: true });
    }
  }, [documentId, navigate, isChatContext]);

  const showsChatReference = isChatContext && !!selectedDocumentReference;

  // A stored source PDF loads through a short-lived signed link (LAR-542).
  const legislationPdf = useLegislationPdfUrl(
    showsChatReference ? null : legislation,
    { logWhenUnavailable: true }
  );

  // Build PDF URL based on context
  const pdfUrl = useMemo(() => {
    if (showsChatReference && selectedDocumentReference) {
      return buildPdfUrlFromReference(selectedDocumentReference);
    }
    if (legislation) {
      return legislationPdf.url;
    }
    return selectedDocumentReference
      ? buildPdfUrlFromReference(selectedDocumentReference)
      : null;
  }, [
    showsChatReference,
    selectedDocumentReference,
    legislation,
    legislationPdf.url,
  ]);

  const fallbackTitle =
    legislation?.title ||
    selectedDocumentReference?.documentTitle ||
    config.defaultTitle;

  const resolveBaseReturnUrl = useCallback(
    () => resolveLegislationViewerReturnUrl(location.pathname, "pdf"),
    [location.pathname]
  );

  const handleExitToLegislationsList = useCallback(() => {
    if (config.context === "legislation") {
      setNavigateBack(true);
    }
  }, [config.context, setNavigateBack]);

  // Only the Kilder viewer has a source list to return to. The navigation
  // stack outlives a viewer-to-viewer navigation, so a chat viewer can be
  // shown a stale datasource crumb — which must not touch the Kilder table.
  const getDatasourceFrameOnClick = useDatasourceBreadcrumbReturn(
    config.context === "legislation" ? datasource : undefined
  );

  const { parentBreadcrumbs, flatParentBreadcrumbs, currentTitle, currentFullTitle, handleViewerBack } =
    useDocumentViewerBreadcrumbs({
      fallbackTitle,
      resolveBaseReturnUrl,
      onExitToList: handleExitToLegislationsList,
      getDatasourceFrameOnClick,
    });

  useDocumentViewerStackSync({
    title: fallbackTitle,
  });

  // Determine if sidebar should be shown
  const shouldShowSidebar = useMemo(() => {
    if (!documentId) return false;
    if (config.sidebarTabs.length === 0) return false;
    if (config.conditionalSidebar) {
      return documentReferences.length > 0;
    }
    return true;
  }, [
    documentId,
    config.sidebarTabs.length,
    config.conditionalSidebar,
    documentReferences.length,
  ]);

  // Get document title
  const documentTitle = useMemo(() => {
    return (
      legislation?.title ||
      selectedDocumentReference?.documentTitle ||
      config.defaultTitle
    );
  }, [
    legislation?.title,
    selectedDocumentReference?.documentTitle,
    config.defaultTitle,
  ]);

  // Get legislation status for badge
  const legislationStatus = useMemo(() => {
    if (!legislation) return undefined;
    return legislation.isCurrent === true
      ? LegislationStatus.Valid
      : LegislationStatus.Historic;
  }, [legislation]);

  const showDocumentHeader = !!(legislation || selectedDocumentReference);

  // Determine which document to pass to EmbedPDFViewer
  const documentForViewer = useMemo(() => {
    // In chat context, prefer the document reference if available
    if (isChatContext && selectedDocumentReference) {
      return selectedDocumentReference;
    }
    // Otherwise use legislation
    return legislation;
  }, [isChatContext, selectedDocumentReference, legislation]);

  // Render sidebar if applicable
  const renderSidebar = () => {
    if (!shouldShowSidebar) {
      return undefined;
    }

    return (
      <DocumentSidebar
        key={documentId}
        contextualDefault={
          config.context === "chat"
            ? DocumentTab.CHAT_REFERENCES
            : undefined
        }
        availableTabs={config.sidebarTabs}
        documentId={documentId}
        selectionResetKey={documentId}
        legislation={legislation}
        createReferenceTarget={{
          documentId,
          source: effectiveDatasource,
        }}
      />
    );
  };

  if (isDeprecatedDatasource && documentId) {
    return (
      <PageContent title="Kilde udfaset" contentClassName="flex flex-col !gap-0">
        <DeprecatedConsolidationDatasourcePage
          documentId={documentId}
          datasource={datasource ?? ""}
        />
      </PageContent>
    );
  }

  return (
    <PageContent
      title={currentTitle}
      titleFull={currentFullTitle}
      parent={parentBreadcrumbs}
      parentOverflowMenu={flatParentBreadcrumbs}
      onNavigateBack={handleViewerBack}
      rightSidebar={renderSidebar()}
    >
      <div className="p-4 xl:px-8 h-[calc(100vh-3rem)] flex flex-col gap-1">
        {pdfUrl && documentForViewer ? (
          <>
            {showDocumentHeader && (
              <DocumentPDFHeader
                legislationStatus={legislationStatus}
                documentTitle={documentTitle}
              />
            )}
            <EmbedPDFViewer
              uri={pdfUrl}
              document={documentForViewer}
              boundingBoxes={isChatContext ? selectedBoundingBoxes : undefined}
              documentCategory={
                isChatContext
                  ? DocumentCategory.LegislationReference
                  : DocumentCategory.Legislation
              }
            />
          </>
        ) : legislation && legislationPdf.isPending ? (
          showDocumentHeader && (
            <DocumentPDFHeader
              legislationStatus={legislationStatus}
              documentTitle={documentTitle}
            />
          )
        ) : legislation && legislationPdf.isError ? (
          <>
            {showDocumentHeader && (
              <DocumentPDFHeader
                legislationStatus={legislationStatus}
                documentTitle={documentTitle}
              />
            )}
            <div className="flex flex-1 min-h-0 items-center justify-center">
              <div className="text-center max-w-md">
                <h2 className="text-xl font-semibold mb-2">
                  PDF&apos;en kunne ikke hentes
                </h2>
                <p className="text-muted-foreground">
                  Lara kunne ikke hente PDF-versionen af denne tekst. Prøv igen
                  om lidt.
                </p>
              </div>
            </div>
          </>
        ) : legislation || documentForViewer ? (
          <>
            {showDocumentHeader && (
              <DocumentPDFHeader
                legislationStatus={legislationStatus}
                documentTitle={documentTitle}
              />
            )}
            <div className="flex flex-1 min-h-0 items-center justify-center">
              <div className="text-center max-w-md">
                <h2 className="text-xl font-semibold mb-2">PDF kan ikke vises</h2>
                <p className="text-muted-foreground">
                  Lara kan ikke åbne en PDF-version af denne tekst.
                </p>
                <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
                  Ser du denne besked, så meld venligst dokumentet som fejlbart i en bug-sag, så vi
                  kan undersøge og rette det.
                </p>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </PageContent>
  );
};

export default UnifiedLegislationPDFView;
